import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  Platform,
  Share,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
  AntDesign,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import axios from 'axios';

const { width, height } = Dimensions.get('window');

// ==================== API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch (e) {}
  return config;
}, (e) => Promise.reject(e));

const getBusinessId = async () => {
  let id = await AsyncStorage.getItem('company_id');
  if (!id) id = await AsyncStorage.getItem('company_id');
  if (!id) {
    try {
      const p = await AsyncStorage.getItem('company_profile');
      if (p) { const parsed = JSON.parse(p); id = parsed?.id ? String(parsed.id) : null; }
    } catch {}
  }
  return id || null;
};

// Default export for the CompanyAnalytics component
export default function CompanyAnalytics({ navigation, route }) {
  // Route params from BusinessDashboard
  const { companyProfile: routeCompanyProfile, subscription: routeSubscription, companyId: routeCompanyId } = route?.params || {};

  // State management
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedMetric, setSelectedMetric] = useState('overview');
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showInsightsModal, setShowInsightsModal] = useState(false);
  const [predictiveData, setPredictiveData] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Company data
  const [companyProfile, setCompanyProfile] = useState(routeCompanyProfile || null);
  const [companyId, setCompanyId] = useState(routeCompanyId || null);
  const [companyLogo, setCompanyLogo] = useState(
    routeCompanyProfile?.logo || routeCompanyProfile?.logo_url || routeCompanyProfile?.company_logo || routeCompanyProfile?.banner_image_url || null
  );
  const [user, setUser] = useState(null);
  
  // Enhanced Analytics data with BI integration
  const [analyticsData, setAnalyticsData] = useState({
    overview: {
      totalRevenue: 2450000,
      revenueGrowth: 12.5,
      totalClients: 45,
      clientGrowth: 8.2,
      activeProjects: 15,
      projectGrowth: 25.0,
      teamMembers: 85,
      teamGrowth: 15.3,
      businessHealth: 85,
      kpiScore: 78
    },
    revenue: {
      monthly: [180000, 195000, 210000, 225000, 240000, 255000],
      quarterly: [585000, 630000, 675000, 720000],
      yearly: [2100000, 2450000],
      breakdown: {
        construction: 65,
        engineering: 25,
        consulting: 10
      },
      forecast: [270000, 285000, 300000],
      seasonality: {
        high: ['Mar', 'Apr', 'Sep', 'Oct'],
        low: ['Jan', 'Feb', 'Dec']
      }
    },
    clients: {
      total: 45,
      new: 8,
      retained: 37,
      satisfaction: 4.7,
      segments: {
        enterprise: 15,
        medium: 20,
        small: 10
      },
      churnRate: 4.2,
      lifetimeValue: 125000,
      acquisitionCost: 15000
    },
    projects: {
      total: 15,
      completed: 12,
      inProgress: 3,
      onTime: 85,
      onBudget: 92,
      categories: {
        commercial: 8,
        residential: 4,
        infrastructure: 3
      },
      profitability: {
        high: ['commercial', 'infrastructure'],
        low: ['residential']
      },
      riskFactors: ['weather_delays', 'supply_chain', 'labor_shortage']
    },
    team: {
      total: 85,
      departments: {
        engineering: 35,
        management: 15,
        finance: 10,
        hr: 8,
        operations: 17
      },
      productivity: 87,
      satisfaction: 4.5,
      retention: 94,
      overtime: 12,
      trainingHours: 240
    },
    financial: {
      profit: 490000,
      profitMargin: 20,
      expenses: 1960000,
      roi: 25,
      cashFlow: 350000,
      burnRate: 125000,
      runway: 28
    },
    market: {
      share: 18.5,
      growth: 12.3,
      competitors: 12,
      position: 'leader',
      trends: ['sustainability', 'digital_transformation', 'prefabrication']
    }
  });

  // Time periods
  const timePeriods = [
    { id: 'week', title: 'This Week', icon: 'calendar-week' },
    { id: 'month', title: 'This Month', icon: 'calendar-month' },
    { id: 'quarter', title: 'This Quarter', icon: 'calendar-range' },
    { id: 'year', title: 'This Year', icon: 'calendar' }
  ];

  // Enhanced metric categories with BI features
  const metricCategories = [
    { id: 'overview', title: 'Overview', icon: 'view-dashboard', color: '#4A90E2' },
    { id: 'revenue', title: 'Revenue', icon: 'currency-usd', color: '#10B981' },
    { id: 'clients', title: 'Clients', icon: 'account-group', color: '#F59E0B' },
    { id: 'projects', title: 'Projects', icon: 'clipboard-list', color: '#8B5CF6' },
    { id: 'team', title: 'Team', icon: 'account-multiple', color: '#EF4444' },
    { id: 'financial', title: 'Financial', icon: 'chart-line', color: '#06B6D4' },
    { id: 'market', title: 'Market', icon: 'trending-up', color: '#EC4899' }
  ];

  // AI Insights data
  const businessInsights = [
    {
      id: 1,
      type: 'opportunity',
      title: 'Revenue Growth Opportunity',
      description: 'Construction segment shows 22% higher profitability than other segments. Consider allocating more resources.',
      impact: 'high',
      confidence: 92,
      action: 'Reallocate resources to construction projects',
      metric: 'revenue'
    },
    {
      id: 2,
      type: 'warning',
      title: 'Client Concentration Risk',
      description: 'Top 3 clients represent 45% of total revenue. Diversify client base to reduce dependency.',
      impact: 'medium',
      confidence: 85,
      action: 'Develop new client acquisition strategy',
      metric: 'clients'
    },
    {
      id: 3,
      type: 'success',
      title: 'Team Productivity Excellence',
      description: 'Engineering team exceeds productivity targets by 18%. Best practices can be shared across departments.',
      impact: 'high',
      confidence: 88,
      action: 'Document and share engineering team practices',
      metric: 'team'
    },
    {
      id: 4,
      type: 'info',
      title: 'Market Position Strengthening',
      description: 'Market share increased by 3.2% this quarter. Competitor analysis shows weakening position of main rival.',
      impact: 'medium',
      confidence: 79,
      action: 'Consider strategic acquisitions',
      metric: 'market'
    }
  ];

  useEffect(() => {
    initializeAnalytics();
    generatePredictiveData();
    generateAIInsights();
  }, [selectedPeriod]);

  // Initialize analytics data — API-first with cache fallback
  const initializeAnalytics = async () => {
    try {
      setIsLoading(true);

      // ── 1. Resolve company ID ──
      const bizId = routeCompanyId || companyId || await getBusinessId();
      if (bizId && !companyId) setCompanyId(bizId);

      // ── 2. Load user data ──
      const userData = await AsyncStorage.getItem('user_data');
      if (userData) { try { setUser(JSON.parse(userData)); } catch {} }

      // ── 3. Load company profile (route → API → cache → default) ──
      if (!companyProfile) {
        const cachedProfile = await AsyncStorage.getItem('company_profile');
        if (cachedProfile) {
          try {
            const p = JSON.parse(cachedProfile);
            setCompanyProfile(p);
            setCompanyLogo(p?.logo || p?.logo_url || p?.company_logo || p?.banner_image_url || null);
          } catch {}
        }
      }
      if (bizId) {
        try {
          const res = await api.get(`/api/company-profile/${parseInt(bizId) || 0}`);
          if (res.data?.success && res.data.data) {
            const raw = Array.isArray(res.data.data) ? res.data.data[0] : res.data.data;
            if (raw) {
              setCompanyProfile(raw);
              setCompanyLogo(raw.logo || raw.logo_url || raw.company_logo || raw.banner_image_url || null);
              await AsyncStorage.setItem('company_profile', JSON.stringify(raw));
            }
          }
        } catch (e) { console.warn('Company profile API error:', e?.message); }
      }

      // ── 4. Fetch live stats from API to populate overview ──
      if (bizId) {
        try {
          const bId = parseInt(bizId) || 0;
          const safeGet = (url, params) => api.get(url, { params }).catch(() => ({ data: { success: false } }));

          const [projRes, clientRes, revenueRes, empRes, vendorRes, invoiceRes] = await Promise.allSettled([
            safeGet('/api/projects', { company_id: bId }),
            safeGet('/api/clients', { company_id: bId }),
            safeGet('/api/financial/revenue', { company_id: bId }),
            safeGet('/api/employees', { company_id: bId }),
            safeGet('/api/vendors', { company_id: bId }),
            safeGet('/api/invoices', { company_id: bId }),
          ]);

          const ext = (r) => (r.status === 'fulfilled' && r.value.data?.success ? r.value.data.data?.length || 0 : 0);
          const revAmt = revenueRes.status === 'fulfilled' && revenueRes.value.data?.success && revenueRes.value.data.data?.length > 0
            ? parseFloat(revenueRes.value.data.data[0].amount || 0) : 0;
          const apiProjects = ext(projRes);
          const apiClients = ext(clientRes);
          const apiTeam = ext(empRes);

          // Merge API data into analytics — keeps mock structure, overrides with real where available
          setAnalyticsData(prev => ({
            ...prev,
            overview: {
              ...prev.overview,
              totalRevenue: revAmt || prev.overview.totalRevenue,
              totalClients: apiClients || prev.overview.totalClients,
              activeProjects: apiProjects || prev.overview.activeProjects,
              teamMembers: apiTeam || prev.overview.teamMembers,
            },
          }));

          console.log(`✅ Analytics loaded: ${apiProjects} projects, ${apiClients} clients, £${revAmt} revenue, ${apiTeam} team`);
        } catch (apiErr) {
          console.warn('Analytics API fetch failed, using defaults:', apiErr?.message);
        }
      }

    } catch (error) {
      console.error('Error initializing analytics:', error);
      Alert.alert('Error', 'Failed to load analytics data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate predictive data based on current trends
  const generatePredictiveData = () => {
    const predictions = {
      nextQuarterRevenue: analyticsData.revenue.quarterly[analyticsData.revenue.quarterly.length - 1] * 1.15,
      clientGrowth: Math.round(analyticsData.clients.total * 1.08),
      projectPipeline: analyticsData.projects.total + 4,
      cashFlowProjection: analyticsData.financial.cashFlow * 1.12,
      riskScore: 22
    };
    setPredictiveData(predictions);
  };

  // Generate AI-powered business insights
  const generateAIInsights = () => {
    const filteredInsights = selectedMetric === 'overview' 
      ? businessInsights 
      : businessInsights.filter(insight => insight.metric === selectedMetric);
    
    setAiInsights(filteredInsights);
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    await initializeAnalytics();
    generatePredictiveData();
    generateAIInsights();
    setRefreshing(false);
  };

  // Safe navigation helper — always pass company context
  const navigateToScreen = (screenName, params = {}) => {
    if (navigation && navigation.navigate) {
      navigation.navigate(screenName, {
        ...params,
        companyProfile,
        companyId,
      });
    } else {
      Alert.alert('Navigation Error', `Cannot navigate to ${screenName}. Navigation not available.`);
    }
  };

  // Format currency - make it more robust
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '£0';
    const numAmount = Number(amount);
    if (isNaN(numAmount)) return '£0';
    
    if (numAmount >= 1000000) {
      return `£${(numAmount / 1000000).toFixed(1)}M`;
    } else if (numAmount >= 1000) {
      return `£${(numAmount / 1000).toFixed(0)}K`;
    } else {
      return `£${numAmount}`;
    }
  };

  // Format percentage - make it more robust
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return '+0.0%';
    const numValue = Number(value);
    if (isNaN(numValue)) return '+0.0%';
    
    return `${numValue > 0 ? '+' : ''}${numValue.toFixed(1)}%`;
  };

  // Get insight color based on type
  const getInsightColor = (type) => {
    switch (type) {
      case 'opportunity': return '#10B981';
      case 'warning': return '#F59E0B';
      case 'success': return '#8B5CF6';
      case 'info': return '#4A90E2';
      default: return '#64748B';
    }
  };

  // Get impact badge color
  const getImpactColor = (impact) => {
    switch (impact) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#64748B';
    }
  };

  // Export as PDF
  const exportAsPDF = async () => {
    try {
      setExportLoading(true);
      
      const reportContent = generateReportContent();
      const fileName = `Business_Report_${new Date().toISOString().split('T')[0]}.txt`;
      
      // For mobile devices
      const fileUri = FileSystem.documentDirectory + fileName;
      
      await FileSystem.writeAsStringAsync(fileUri, reportContent, {
        encoding: FileSystem.EncodingType.UTF8
      });
      
      // Check if sharing is available
      const isSharingAvailable = await Sharing.isAvailableAsync();
      
      if (isSharingAvailable) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'text/plain',
          dialogTitle: 'Business Intelligence Report',
          UTI: 'public.plain-text'
        });
      } else {
        // Fallback: show alert with content
        Alert.alert(
          'Report Generated', 
          'Business report has been saved. You can find it in your app documents.',
          [{ text: 'OK' }]
        );
      }
      
    } catch (error) {
      console.error('Error exporting report:', error);
      Alert.alert(
        'Export Complete', 
        'Your business intelligence report has been prepared. You can save it from the shared options.',
        [{ text: 'OK' }]
      );
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  // Export as Excel/CSV
  const exportAsExcel = async () => {
    try {
      setExportLoading(true);
      
      const csvContent = generateCSVContent();
      const fileName = `Business_Data_${new Date().toISOString().split('T')[0]}.csv`;
      
      const fileUri = FileSystem.documentDirectory + fileName;
      
      await FileSystem.writeAsStringAsync(fileUri, csvContent, {
        encoding: FileSystem.EncodingType.UTF8
      });
      
      const isSharingAvailable = await Sharing.isAvailableAsync();
      
      if (isSharingAvailable) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'text/csv',
          dialogTitle: 'Business Data Export',
          UTI: 'public.comma-separated-values-text'
        });
      } else {
        Alert.alert(
          'Data Exported', 
          'Business data has been saved as CSV file.',
          [{ text: 'OK' }]
        );
      }
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      Alert.alert(
        'Data Ready', 
        'Your business data is ready for export.',
        [{ text: 'OK' }]
      );
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  // Executive Summary
  const exportExecutiveSummary = async () => {
    try {
      setExportLoading(true);
      
      const summaryContent = generateExecutiveSummary();
      const fileName = `Executive_Summary_${new Date().toISOString().split('T')[0]}.txt`;
      
      const fileUri = FileSystem.documentDirectory + fileName;
      
      await FileSystem.writeAsStringAsync(fileUri, summaryContent, {
        encoding: FileSystem.EncodingType.UTF8
      });
      
      const isSharingAvailable = await Sharing.isAvailableAsync();
      
      if (isSharingAvailable) {
        await Sharing.shareAsync(fileUri, {
          mimeType: 'text/plain',
          dialogTitle: 'Executive Summary',
          UTI: 'public.plain-text'
        });
      } else {
        Alert.alert(
          'Summary Exported', 
          'Executive summary has been saved successfully.',
          [{ text: 'OK' }]
        );
      }
      
    } catch (error) {
      console.error('Error exporting executive summary:', error);
      Alert.alert(
        'Summary Ready', 
        'Executive summary is ready for sharing.',
        [{ text: 'OK' }]
      );
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  // Email Report - Simplified version
  const emailReport = async () => {
    try {
      const summaryContent = generateExecutiveSummary();
      const subject = `Business Intelligence Report - ${companyProfile?.company_name || companyProfile?.name || 'JOBENG Construction Ltd'}`;
      
      // Use Share API as fallback for email
      await Share.share({
        title: subject,
        message: summaryContent,
      });
      
    } catch (error) {
      console.error('Error with email:', error);
      Alert.alert(
        'Share Report', 
        'You can share the report using your device\'s share options.',
        [{ text: 'OK' }]
      );
    } finally {
      setShowExportModal(false);
    }
  };

  // Generate Report Content
  const generateReportContent = () => {
    try {
      const currentDate = new Date().toLocaleDateString();
      const companyName = companyProfile?.company_name || companyProfile?.name || user?.company_name || 'JOBENG Construction Ltd';
      const period = timePeriods.find(p => p.id === selectedPeriod)?.title || 'This Month';
      
      return `
BUSINESS INTELLIGENCE REPORT
============================

Company: ${companyName}
Date: ${currentDate}
Period: ${period}

KEY METRICS:
• Revenue: ${formatCurrency(analyticsData.overview.totalRevenue || 0)} 
• Clients: ${analyticsData.overview.totalClients || 0}
• Projects: ${analyticsData.overview.activeProjects || 0}
• Team: ${analyticsData.overview.teamMembers || 0}

FINANCIAL OVERVIEW:
• Profit: ${formatCurrency(analyticsData.financial.profit || 0)}
• Margin: ${analyticsData.financial.profitMargin || 0}%
• ROI: ${analyticsData.financial.roi || 0}%

TOP INSIGHTS:
${(aiInsights || []).slice(0, 3).map(insight => `
• ${insight.title}
  ${insight.description}
`).join('')}

---
Generated by Business Intelligence System
      `.trim();
    } catch (error) {
      console.error('Error generating report:', error);
      return 'Business Intelligence Report - Error generating content';
    }
  };

  // Generate CSV Content
  const generateCSVContent = () => {
    try {
      const headers = ['Category', 'Metric', 'Value'];
      const rows = [
        ['Revenue', 'Total', analyticsData.overview.totalRevenue || 0],
        ['Clients', 'Total', analyticsData.overview.totalClients || 0],
        ['Projects', 'Active', analyticsData.overview.activeProjects || 0],
        ['Team', 'Members', analyticsData.overview.teamMembers || 0],
        ['Financial', 'Profit', analyticsData.financial.profit || 0],
        ['Financial', 'Margin', analyticsData.financial.profitMargin || 0],
      ];
      
      const csvRows = [headers, ...rows];
      return csvRows.map(row => 
        row.map(field => `"${String(field || '').replace(/"/g, '""')}"`).join(',')
      ).join('\n');
    } catch (error) {
      console.error('Error generating CSV:', error);
      return 'Category,Metric,Value\nError,Generating Data,0';
    }
  };

  // Generate Executive Summary
  const generateExecutiveSummary = () => {
    try {
      const currentDate = new Date().toLocaleDateString();
      const companyName = companyProfile?.company_name || companyProfile?.name || user?.company_name || 'JOBENG Construction Ltd';
      
      return `
EXECUTIVE SUMMARY
Company: ${companyName}
Date: ${currentDate}

Performance Overview:
• Revenue Growth: ${formatPercentage(analyticsData.overview.revenueGrowth || 0)}
• Client Growth: ${formatPercentage(analyticsData.overview.clientGrowth || 0)}
• Business Health: ${analyticsData.overview.businessHealth || 0}/100

Key Metrics:
${(aiInsights || []).slice(0, 2).map(insight => `
- ${insight.title}
`).join('')}

Generated by Business Intelligence System
      `.trim();
    } catch (error) {
      console.error('Error generating summary:', error);
      return 'Executive Summary - Error generating content';
    }
  };

  // Render header with enhanced BI features
  const renderHeader = () => (
    <LinearGradient
      colors={['#4A90E2', '#357ABD']}
      style={styles.header}
    >
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      <View style={styles.headerContent}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation?.goBack?.()}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        
        <View style={styles.headerInfo}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            {companyLogo ? (
              <Image source={{ uri: companyLogo || undefined }} style={{ width: 30, height: 30, borderRadius: 15, marginRight: 10, borderWidth: 1.5, borderColor: 'rgba(255,255,255,0.4)' }} />
            ) : null}
            <View>
              <Text style={styles.headerTitle}>Business Intelligence</Text>
              <Text style={styles.headerSubtitle}>
                {companyProfile?.company_name || companyProfile?.name || user?.company_name || 'JOBENG Construction Ltd'}
              </Text>
            </View>
          </View>
        </View>
        
        <View style={styles.headerActions}>
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowInsightsModal(true)}
          >
            <MaterialCommunityIcons name="robot" size={20} color="#fff" />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowPeriodModal(true)}
          >
            <MaterialCommunityIcons name="calendar-range" size={20} color="#fff" />
          </TouchableOpacity>
          
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={() => setShowExportModal(true)}
          >
            <MaterialCommunityIcons name="download" size={20} color="#fff" />
          </TouchableOpacity>
        </View>
      </View>
      
      {/* Period Selector */}
      <View style={styles.periodSelector}>
        <Text style={styles.periodText}>
          {timePeriods.find(p => p.id === selectedPeriod)?.title || 'This Month'}
        </Text>
        <MaterialCommunityIcons name="chevron-down" size={16} color="rgba(255, 255, 255, 0.8)" />
      </View>

      {/* Quick Stats Bar */}
      <View style={styles.quickStats}>
        <View style={styles.quickStat}>
          <Text style={styles.quickStatValue}>{analyticsData.overview.businessHealth || 0}</Text>
          <Text style={styles.quickStatLabel}>Health Score</Text>
        </View>
        <View style={styles.quickStat}>
          <Text style={styles.quickStatValue}>{predictiveData?.riskScore || 22}</Text>
          <Text style={styles.quickStatLabel}>Risk Score</Text>
        </View>
        <View style={styles.quickStat}>
          <Text style={styles.quickStatValue}>{aiInsights.length}</Text>
          <Text style={styles.quickStatLabel}>AI Insights</Text>
        </View>
      </View>
    </LinearGradient>
  );

  // Render metric tabs
  const renderMetricTabs = () => (
    <View style={styles.metricTabs}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        {metricCategories.map(category => (
          <TouchableOpacity
            key={category.id}
            style={[
              styles.metricTab,
              selectedMetric === category.id && styles.metricTabActive
            ]}
            onPress={() => setSelectedMetric(category.id)}
          >
            <MaterialCommunityIcons 
              name={category.icon} 
              size={18} 
              color={selectedMetric === category.id ? category.color : '#64748B'} 
            />
            <Text style={[
              styles.metricTabText,
              selectedMetric === category.id && { color: category.color }
            ]}>
              {category.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // Render enhanced overview metrics with BI features
  const renderOverviewMetrics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Business Intelligence Dashboard</Text>
      
      {/* Predictive Analytics Card */}
      <View style={styles.predictiveCard}>
        <View style={styles.predictiveHeader}>
          <MaterialCommunityIcons name="crystal-ball" size={24} color="#8B5CF6" />
          <Text style={styles.predictiveTitle}>Predictive Analytics</Text>
        </View>
        <View style={styles.predictiveGrid}>
          <View style={styles.predictiveItem}>
            <Text style={styles.predictiveValue}>
              {formatCurrency(predictiveData?.nextQuarterRevenue || 0)}
            </Text>
            <Text style={styles.predictiveLabel}>Next Quarter Revenue</Text>
            <View style={styles.confidenceBadge}>
              <Text style={styles.confidenceText}>88% confidence</Text>
            </View>
          </View>
          <View style={styles.predictiveItem}>
            <Text style={styles.predictiveValue}>{predictiveData?.clientGrowth || 0}</Text>
            <Text style={styles.predictiveLabel}>Projected Clients</Text>
            <View style={styles.confidenceBadge}>
              <Text style={styles.confidenceText}>82% confidence</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.metricsGrid}>
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <MaterialCommunityIcons name="currency-usd" size={24} color="#10B981" />
            <View style={[styles.trendIndicator, { backgroundColor: '#10B981' }]}>
              <Ionicons name="trending-up" size={12} color="#fff" />
            </View>
          </View>
          <Text style={styles.metricValue}>{formatCurrency(analyticsData.overview.totalRevenue)}</Text>
          <Text style={styles.metricLabel}>Total Revenue</Text>
          <Text style={[styles.metricChange, { color: '#10B981' }]}>
            {formatPercentage(analyticsData.overview.revenueGrowth)}
          </Text>
        </View>

        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <MaterialCommunityIcons name="account-group" size={24} color="#F59E0B" />
            <View style={[styles.trendIndicator, { backgroundColor: '#F59E0B' }]}>
              <Ionicons name="trending-up" size={12} color="#fff" />
            </View>
          </View>
          <Text style={styles.metricValue}>{analyticsData.overview.totalClients || 0}</Text>
          <Text style={styles.metricLabel}>Total Clients</Text>
          <Text style={[styles.metricChange, { color: '#10B981' }]}>
            {formatPercentage(analyticsData.overview.clientGrowth)}
          </Text>
        </View>

        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <MaterialCommunityIcons name="clipboard-list" size={24} color="#8B5CF6" />
            <View style={[styles.trendIndicator, { backgroundColor: '#8B5CF6' }]}>
              <Ionicons name="trending-up" size={12} color="#fff" />
            </View>
          </View>
          <Text style={styles.metricValue}>{analyticsData.overview.activeProjects || 0}</Text>
          <Text style={styles.metricLabel}>Active Projects</Text>
          <Text style={[styles.metricChange, { color: '#10B981' }]}>
            {formatPercentage(analyticsData.overview.projectGrowth)}
          </Text>
        </View>

        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <MaterialCommunityIcons name="account-multiple" size={24} color="#EF4444" />
            <View style={[styles.trendIndicator, { backgroundColor: '#EF4444' }]}>
              <Ionicons name="trending-up" size={12} color="#fff" />
            </View>
          </View>
          <Text style={styles.metricValue}>{analyticsData.overview.teamMembers || 0}</Text>
          <Text style={styles.metricLabel}>Team Members</Text>
          <Text style={[styles.metricChange, { color: '#10B981' }]}>
            {formatPercentage(analyticsData.overview.teamGrowth)}
          </Text>
        </View>
      </View>

      {/* Business Health Score */}
      <View style={styles.healthScoreCard}>
        <Text style={styles.healthScoreTitle}>Business Health Score</Text>
        <View style={styles.healthScoreContent}>
          <View style={styles.scoreCircle}>
            <Text style={styles.scoreValue}>{analyticsData.overview.businessHealth || 0}</Text>
            <Text style={styles.scoreLabel}>/100</Text>
          </View>
          <View style={styles.healthIndicators}>
            <View style={styles.healthIndicator}>
              <Text style={styles.healthIndicatorLabel}>Financial</Text>
              <View style={styles.healthBar}>
                <View style={[styles.healthFill, { width: '85%', backgroundColor: '#10B981' }]} />
              </View>
            </View>
            <View style={styles.healthIndicator}>
              <Text style={styles.healthIndicatorLabel}>Operational</Text>
              <View style={styles.healthBar}>
                <View style={[styles.healthFill, { width: '78%', backgroundColor: '#8B5CF6' }]} />
              </View>
            </View>
            <View style={styles.healthIndicator}>
              <Text style={styles.healthIndicatorLabel}>Strategic</Text>
              <View style={styles.healthBar}>
                <View style={[styles.healthFill, { width: '92%', backgroundColor: '#F59E0B' }]} />
              </View>
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  // Render enhanced revenue analytics with BI
  const renderRevenueAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Revenue Intelligence</Text>
      
      {/* Revenue Forecast */}
      <View style={styles.forecastCard}>
        <View style={styles.forecastHeader}>
          <MaterialCommunityIcons name="chart-line" size={24} color="#10B981" />
          <Text style={styles.forecastTitle}>Revenue Forecast</Text>
        </View>
        <View style={styles.forecastItems}>
          {analyticsData.revenue.forecast.map((amount, index) => (
            <View key={index} style={styles.forecastItem}>
              <Text style={styles.forecastPeriod}>Month {index + 1}</Text>
              <Text style={styles.forecastAmount}>{formatCurrency(amount)}</Text>
              <View style={styles.forecastTrend}>
                <Ionicons name="trending-up" size={16} color="#10B981" />
                <Text style={styles.forecastChange}>+8.2%</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Seasonality Insights */}
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <MaterialCommunityIcons name="lightbulb" size={20} color="#F59E0B" />
          <Text style={styles.insightTitle}>Seasonality Pattern</Text>
        </View>
        <Text style={styles.insightText}>
          High revenue months: {analyticsData.revenue.seasonality.high.join(', ')}
        </Text>
        <Text style={styles.insightText}>
          Plan resource allocation accordingly to maximize peak periods.
        </Text>
      </View>
    </View>
  );

  // Render enhanced client analytics with BI
  const renderClientAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Client Intelligence</Text>
      
      <View style={styles.clientStatsGrid}>
        <View style={styles.clientStatCard}>
          <MaterialCommunityIcons name="account-plus" size={32} color="#10B981" />
          <Text style={styles.clientStatValue}>{analyticsData.clients.new || 0}</Text>
          <Text style={styles.clientStatLabel}>New Clients</Text>
        </View>
        
        <View style={styles.clientStatCard}>
          <MaterialCommunityIcons name="account-check" size={32} color="#8B5CF6" />
          <Text style={styles.clientStatValue}>{analyticsData.clients.retained || 0}</Text>
          <Text style={styles.clientStatLabel}>Retained Clients</Text>
        </View>
        
        <View style={styles.clientStatCard}>
          <MaterialCommunityIcons name="star" size={32} color="#F59E0B" />
          <Text style={styles.clientStatValue}>{analyticsData.clients.satisfaction || 0}</Text>
          <Text style={styles.clientStatLabel}>Satisfaction</Text>
        </View>
      </View>

      {/* Client Lifetime Value */}
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <MaterialCommunityIcons name="chart-bar" size={20} color="#10B981" />
          <Text style={styles.insightTitle}>Customer Analytics</Text>
        </View>
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>{formatCurrency(analyticsData.clients.lifetimeValue || 0)}</Text>
            <Text style={styles.analyticsLabel}>Lifetime Value</Text>
          </View>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>{formatCurrency(analyticsData.clients.acquisitionCost || 0)}</Text>
            <Text style={styles.analyticsLabel}>Acquisition Cost</Text>
          </View>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>{analyticsData.clients.churnRate || 0}%</Text>
            <Text style={styles.analyticsLabel}>Churn Rate</Text>
          </View>
        </View>
      </View>
    </View>
  );

  // Render enhanced project analytics with BI
  const renderProjectAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Project Intelligence</Text>
      
      {/* Risk Assessment */}
      <View style={styles.riskCard}>
        <View style={styles.riskHeader}>
          <MaterialCommunityIcons name="alert" size={24} color="#EF4444" />
          <Text style={styles.riskTitle}>Risk Assessment</Text>
        </View>
        <View style={styles.riskFactors}>
          {analyticsData.projects.riskFactors.map((factor, index) => (
            <View key={index} style={styles.riskFactor}>
              <View style={styles.riskIndicator} />
              <Text style={styles.riskText}>{factor.replace('_', ' ').toUpperCase()}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* Profitability Analysis */}
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <MaterialCommunityIcons name="chart-pie" size={20} color="#8B5CF6" />
          <Text style={styles.insightTitle}>Profitability Analysis</Text>
        </View>
        <Text style={styles.insightText}>
          High profitability: {analyticsData.projects.profitability.high.join(', ')}
        </Text>
        <Text style={styles.insightText}>
          Focus on {analyticsData.projects.profitability.high[0]} projects for maximum ROI.
        </Text>
      </View>
    </View>
  );

  // Render team analytics with BI enhancements
  const renderTeamAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Team Intelligence</Text>
      
      <View style={styles.teamOverview}>
        <View style={styles.teamMetric}>
          <Text style={styles.teamMetricValue}>{analyticsData.team.productivity || 0}%</Text>
          <Text style={styles.teamMetricLabel}>Productivity</Text>
          <View style={styles.teamProgressBar}>
            <View style={[styles.teamProgressFill, { width: `${analyticsData.team.productivity || 0}%` }]} />
          </View>
        </View>
        
        <View style={styles.teamMetric}>
          <Text style={styles.teamMetricValue}>{analyticsData.team.satisfaction || 0}</Text>
          <Text style={styles.teamMetricLabel}>Satisfaction</Text>
          <View style={styles.starRating}>
            {[1, 2, 3, 4, 5].map(star => (
              <Ionicons 
                key={star} 
                name={star <= (analyticsData.team.satisfaction || 0) ? "star" : "star-outline"} 
                size={14} 
                color="#F59E0B" 
              />
            ))}
          </View>
        </View>
        
        <View style={styles.teamMetric}>
          <Text style={styles.teamMetricValue}>{analyticsData.team.retention || 0}%</Text>
          <Text style={styles.teamMetricLabel}>Retention</Text>
          <View style={styles.teamProgressBar}>
            <View style={[styles.teamProgressFill, { width: `${analyticsData.team.retention || 0}%` }]} />
          </View>
        </View>
      </View>

      {/* Team Performance Insights */}
      <View style={styles.insightCard}>
        <View style={styles.insightHeader}>
          <MaterialCommunityIcons name="chart-line" size={20} color="#10B981" />
          <Text style={styles.insightTitle}>Performance Insights</Text>
        </View>
        <View style={styles.analyticsGrid}>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>{analyticsData.team.overtime || 0}%</Text>
            <Text style={styles.analyticsLabel}>Overtime</Text>
          </View>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>{analyticsData.team.trainingHours || 0}</Text>
            <Text style={styles.analyticsLabel}>Training Hours</Text>
          </View>
          <View style={styles.analyticsItem}>
            <Text style={styles.analyticsValue}>87%</Text>
            <Text style={styles.analyticsLabel}>Skill Match</Text>
          </View>
        </View>
      </View>

      {/* Department Breakdown */}
      <View style={styles.departmentCard}>
        <Text style={styles.departmentTitle}>Department Distribution</Text>
        <View style={styles.departmentItems}>
          {Object.entries(analyticsData.team.departments).map(([dept, count]) => (
            <View key={dept} style={styles.departmentItem}>
              <Text style={styles.departmentLabel}>
                {dept.charAt(0).toUpperCase() + dept.slice(1)}
              </Text>
              <View style={styles.departmentBar}>
                <View style={[
                  styles.departmentFill, 
                  { width: `${(count / (analyticsData.team.total || 1)) * 100}%` }
                ]} />
              </View>
              <Text style={styles.departmentCount}>{count}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  // Render financial analytics with BI enhancements
  const renderFinancialAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Financial Intelligence</Text>
      
      <View style={styles.financialGrid}>
        <View style={styles.financialCard}>
          <MaterialCommunityIcons name="trending-up" size={24} color="#10B981" />
          <Text style={styles.financialValue}>{formatCurrency(analyticsData.financial.profit || 0)}</Text>
          <Text style={styles.financialLabel}>Net Profit</Text>
          <Text style={styles.financialChange}>+15.2%</Text>
        </View>
        
        <View style={styles.financialCard}>
          <MaterialCommunityIcons name="percent" size={24} color="#8B5CF6" />
          <Text style={styles.financialValue}>{analyticsData.financial.profitMargin || 0}%</Text>
          <Text style={styles.financialLabel}>Profit Margin</Text>
          <Text style={styles.financialChange}>+2.1%</Text>
        </View>
        
        <View style={styles.financialCard}>
          <MaterialCommunityIcons name="chart-line" size={24} color="#F59E0B" />
          <Text style={styles.financialValue}>{analyticsData.financial.roi || 0}%</Text>
          <Text style={styles.financialLabel}>ROI</Text>
          <Text style={styles.financialChange}>+5.3%</Text>
        </View>
        
        <View style={styles.financialCard}>
          <MaterialCommunityIcons name="cash-flow" size={24} color="#06B6D4" />
          <Text style={styles.financialValue}>{formatCurrency(analyticsData.financial.cashFlow || 0)}</Text>
          <Text style={styles.financialLabel}>Cash Flow</Text>
          <Text style={styles.financialChange}>+8.7%</Text>
        </View>
      </View>

      {/* Financial Health Metrics */}
      <View style={styles.healthMetrics}>
        <View style={styles.healthMetric}>
          <Text style={styles.healthMetricValue}>{formatCurrency(analyticsData.financial.burnRate || 0)}</Text>
          <Text style={styles.healthMetricLabel}>Monthly Burn Rate</Text>
        </View>
        <View style={styles.healthMetric}>
          <Text style={styles.healthMetricValue}>{analyticsData.financial.runway || 0}</Text>
          <Text style={styles.healthMetricLabel}>Months Runway</Text>
        </View>
      </View>

      {/* Financial Summary */}
      <View style={styles.financialSummary}>
        <Text style={styles.financialSummaryTitle}>Financial Health Score</Text>
        <View style={styles.healthScore}>
          <Text style={styles.healthScoreValue}>85</Text>
          <Text style={styles.healthScoreLabel}>Excellent</Text>
        </View>
        <View style={styles.healthIndicators}>
          <View style={styles.healthIndicator}>
            <Text style={styles.healthIndicatorLabel}>Liquidity</Text>
            <View style={styles.healthBar}>
              <View style={[styles.healthFill, { width: '90%', backgroundColor: '#10B981' }]} />
            </View>
          </View>
          <View style={styles.healthIndicator}>
            <Text style={styles.healthIndicatorLabel}>Profitability</Text>
            <View style={styles.healthBar}>
              <View style={[styles.healthFill, { width: '85%', backgroundColor: '#8B5CF6' }]} />
            </View>
          </View>
          <View style={styles.healthIndicator}>
            <Text style={styles.healthIndicatorLabel}>Growth</Text>
            <View style={styles.healthBar}>
              <View style={[styles.healthFill, { width: '80%', backgroundColor: '#F59E0B' }]} />
            </View>
          </View>
        </View>
      </View>
    </View>
  );

  // Render market intelligence
  const renderMarketAnalytics = () => (
    <View style={styles.metricsContainer}>
      <Text style={styles.sectionTitle}>Market Intelligence</Text>
      
      <View style={styles.marketStats}>
        <View style={styles.marketStat}>
          <Text style={styles.marketStatValue}>{analyticsData.market.share || 0}%</Text>
          <Text style={styles.marketStatLabel}>Market Share</Text>
        </View>
        <View style={styles.marketStat}>
          <Text style={styles.marketStatValue}>{analyticsData.market.growth || 0}%</Text>
          <Text style={styles.marketStatLabel}>Market Growth</Text>
        </View>
        <View style={styles.marketStat}>
          <Text style={styles.marketStatValue}>{analyticsData.market.competitors || 0}</Text>
          <Text style={styles.marketStatLabel}>Competitors</Text>
        </View>
      </View>

      {/* Market Position */}
      <View style={styles.positionCard}>
        <Text style={styles.positionTitle}>Market Position: {analyticsData.market.position || 'Unknown'}</Text>
        <View style={styles.positionMeter}>
          <View style={[styles.positionFill, { width: '80%' }]} />
        </View>
        <Text style={styles.positionDescription}>
          Leading position in construction sector with strong brand recognition
        </Text>
      </View>

      {/* Industry Trends */}
      <View style={styles.trendsCard}>
        <Text style={styles.trendsTitle}>Industry Trends</Text>
        <View style={styles.trendsList}>
          {analyticsData.market.trends.map((trend, index) => (
            <View key={index} style={styles.trendItem}>
              <MaterialCommunityIcons name="trending-up" size={16} color="#10B981" />
              <Text style={styles.trendText}>{trend.replace('_', ' ')}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  // Render AI Insights modal
  const renderInsightsModal = () => (
    <Modal
      animationType="slide"
      transparent={true}
      visible={showInsightsModal}
      onRequestClose={() => setShowInsightsModal(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.insightsModalContainer}>
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleRow}>
              <MaterialCommunityIcons name="robot" size={24} color="#4A90E2" />
              <Text style={styles.modalTitle}>AI Business Insights</Text>
            </View>
            <TouchableOpacity onPress={() => setShowInsightsModal(false)}>
              <Ionicons name="close" size={24} color="#64748B" />
            </TouchableOpacity>
          </View>
          
          <FlatList
            data={aiInsights}
            keyExtractor={(item) => item.id.toString()}
            renderItem={({ item }) => (
              <View style={styles.insightItem}>
                <View style={[styles.insightIcon, { backgroundColor: getInsightColor(item.type) }]}>
                  <MaterialCommunityIcons 
                    name={
                      item.type === 'opportunity' ? 'lightbulb' :
                      item.type === 'warning' ? 'alert' :
                      item.type === 'success' ? 'check-circle' : 'information'
                    } 
                    size={16} 
                    color="#fff" 
                  />
                </View>
                <View style={styles.insightContent}>
                  <Text style={styles.insightItemTitle}>{item.title}</Text>
                  <Text style={styles.insightDescription}>{item.description}</Text>
                  <Text style={styles.insightAction}>{item.action}</Text>
                  <View style={styles.insightFooter}>
                    <View style={[styles.impactBadge, { backgroundColor: getImpactColor(item.impact) }]}>
                      <Text style={styles.impactText}>{String(item.impact ?? '').toUpperCase()} IMPACT</Text>
                    </View>
                    <Text style={styles.confidenceText}>{item.confidence}% confidence</Text>
                  </View>
                </View>
              </View>
            )}
            contentContainerStyle={styles.insightsList}
          />
        </View>
      </View>
    </Modal>
  );

  // Render content based on selected metric
  const renderContent = () => {
    switch (selectedMetric) {
      case 'overview': return renderOverviewMetrics();
      case 'revenue': return renderRevenueAnalytics();
      case 'clients': return renderClientAnalytics();
      case 'projects': return renderProjectAnalytics();
      case 'team': return renderTeamAnalytics();
      case 'financial': return renderFinancialAnalytics();
      case 'market': return renderMarketAnalytics();
      default: return renderOverviewMetrics();
    }
  };

  // Loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.loadingContainer}>
          <MaterialCommunityIcons name="robot" size={48} color="#4A90E2" />
          <Text style={styles.loadingText}>Loading Business Intelligence...</Text>
          <Text style={styles.loadingSubtext}>Analyzing data patterns and generating insights</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      {renderHeader()}
      {renderMetricTabs()}
      
      <ScrollView 
        style={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {renderContent()}
        
        {/* AI Insights Section */}
        {aiInsights.length > 0 && (
          <View style={styles.insightsSection}>
            <View style={styles.insightsHeader}>
              <Text style={styles.insightsTitle}>AI Insights</Text>
              <TouchableOpacity onPress={() => setShowInsightsModal(true)}>
                <Text style={styles.seeAllText}>See All</Text>
              </TouchableOpacity>
            </View>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {aiInsights.slice(0, 3).map(insight => (
                <View key={insight.id} style={styles.insightCard}>
                  <View style={styles.insightHeader}>
                    <MaterialCommunityIcons 
                      name="lightbulb" 
                      size={16} 
                      color={getInsightColor(insight.type)} 
                    />
                    <Text style={styles.insightTitle}>{insight.title}</Text>
                  </View>
                  <Text style={styles.insightText} numberOfLines={2}>
                    {insight.description}
                  </Text>
                </View>
              ))}
            </ScrollView>
          </View>
        )}
      </ScrollView>

      {renderInsightsModal()}

      {/* Period Selection Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showPeriodModal}
        onRequestClose={() => setShowPeriodModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Time Period</Text>
              <TouchableOpacity onPress={() => setShowPeriodModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.modalContent}>
              {timePeriods.map(period => (
                <TouchableOpacity
                  key={period.id}
                  style={[
                    styles.periodOption,
                    selectedPeriod === period.id && styles.periodOptionSelected
                  ]}
                  onPress={() => {
                    setSelectedPeriod(period.id);
                    setShowPeriodModal(false);
                  }}
                >
                  <MaterialCommunityIcons 
                    name={period.icon} 
                    size={20} 
                    color={selectedPeriod === period.id ? '#4A90E2' : '#64748B'} 
                  />
                  <Text style={[
                    styles.periodOptionText,
                    selectedPeriod === period.id && styles.periodOptionTextSelected
                  ]}>
                    {period.title}
                  </Text>
                  {selectedPeriod === period.id && (
                    <Ionicons name="checkmark" size={20} color="#4A90E2" />
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </View>
      </Modal>

      {/* Export Modal */}
      <Modal
        animationType="slide"
        transparent={true}
        visible={showExportModal}
        onRequestClose={() => setShowExportModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Export Business Intelligence</Text>
              <TouchableOpacity onPress={() => setShowExportModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.modalContent}>
              <TouchableOpacity 
                style={styles.exportOption}
                onPress={exportAsPDF}
                disabled={exportLoading}
              >
                <MaterialCommunityIcons name="file-pdf-box" size={24} color="#E91E63" />
                <View style={styles.exportOptionInfo}>
                  <Text style={styles.exportOptionText}>Export as PDF Report</Text>
                  <Text style={styles.exportOptionSubtext}>Comprehensive analysis with insights</Text>
                </View>
                {exportLoading && <ActivityIndicator size={20} color="#E91E63" />}
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.exportOption}
                onPress={exportAsExcel}
                disabled={exportLoading}
              >
                <MaterialCommunityIcons name="microsoft-excel" size={24} color="#10B981" />
                <View style={styles.exportOptionInfo}>
                  <Text style={styles.exportOptionText}>Export as Excel/CSV</Text>
                  <Text style={styles.exportOptionSubtext}>Raw data with calculations</Text>
                </View>
                {exportLoading && <ActivityIndicator size={20} color="#10B981" />}
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.exportOption}
                onPress={exportExecutiveSummary}
                disabled={exportLoading}
              >
                <MaterialCommunityIcons name="presentation" size={24} color="#4A90E2" />
                <View style={styles.exportOptionInfo}>
                  <Text style={styles.exportOptionText}>Executive Summary</Text>
                  <Text style={styles.exportOptionSubtext}>Key insights for leadership</Text>
                </View>
                {exportLoading && <ActivityIndicator size={20} color="#4A90E2" />}
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.exportOption}
                onPress={emailReport}
                disabled={exportLoading}
              >
                <MaterialCommunityIcons name="email" size={24} color="#F59E0B" />
                <View style={styles.exportOptionInfo}>
                  <Text style={styles.exportOptionText}>Email Report</Text>
                  <Text style={styles.exportOptionSubtext}>Share via email with stakeholders</Text>
                </View>
                {exportLoading && <ActivityIndicator size={20} color="#F59E0B" />}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
  },
  loadingSubtext: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 8,
    textAlign: 'center',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  headerActions: {
    flexDirection: 'row',
    gap: 8,
  },
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  periodSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    alignSelf: 'center',
  },
  periodText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginRight: 8,
  },
  quickStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 16,
  },
  quickStat: {
    alignItems: 'center',
  },
  quickStatValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
    marginBottom: 4,
  },
  quickStatLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
  },
  metricTabs: {
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  metricTab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
  },
  metricTabActive: {
    borderBottomWidth: 2,
    borderBottomColor: '#4A90E2',
  },
  metricTabText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  content: {
    flex: 1,
  },
  metricsContainer: {
    padding: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  metricCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    width: (width - 52) / 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  metricHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  trendIndicator: {
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  metricLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 8,
  },
  metricChange: {
    fontSize: 12,
    fontWeight: '600',
  },
  // Predictive Analytics Styles
  predictiveCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  predictiveHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  predictiveTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 8,
  },
  predictiveGrid: {
    flexDirection: 'row',
    gap: 16,
  },
  predictiveItem: {
    flex: 1,
  },
  predictiveValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  predictiveLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 8,
  },
  confidenceBadge: {
    backgroundColor: '#F0F8FF',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  confidenceText: {
    fontSize: 10,
    color: '#4A90E2',
    fontWeight: '500',
  },
  // Health Score Styles
  healthScoreCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  healthScoreTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  healthScoreContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scoreCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#10B981',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 20,
  },
  scoreValue: {
    fontSize: 24,
    fontWeight: '700',
    color: '#fff',
  },
  scoreLabel: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  healthIndicators: {
    flex: 1,
    gap: 12,
  },
  healthIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  healthIndicatorLabel: {
    fontSize: 14,
    color: '#0F172A',
    width: 80,
  },
  healthBar: {
    flex: 1,
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  healthFill: {
    height: '100%',
    borderRadius: 4,
  },
  // Forecast Styles
  forecastCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  forecastHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  forecastTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 8,
  },
  forecastItems: {
    gap: 12,
  },
  forecastItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  forecastPeriod: {
    fontSize: 14,
    color: '#64748B',
    width: 80,
  },
  forecastAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  forecastTrend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  forecastChange: {
    fontSize: 12,
    color: '#10B981',
    fontWeight: '500',
  },
  // Insight Card Styles
  insightCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  insightTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 8,
  },
  insightText: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  // Risk Card Styles
  riskCard: {
    backgroundColor: '#FEF2F2',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#EF4444',
  },
  riskHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  riskTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginLeft: 8,
  },
  riskFactors: {
    gap: 8,
  },
  riskFactor: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  riskIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#EF4444',
  },
  riskText: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
  },
  // Analytics Grid Styles
  analyticsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  analyticsItem: {
    flex: 1,
    alignItems: 'center',
  },
  analyticsValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  analyticsLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  // Market Styles
  marketStats: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  marketStat: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  marketStatValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  marketStatLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  positionCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  positionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  positionMeter: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginBottom: 12,
    overflow: 'hidden',
  },
  positionFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 4,
  },
  positionDescription: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  trendsCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  trendsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  trendsList: {
    gap: 8,
  },
  trendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  trendText: {
    fontSize: 14,
    color: '#64748B',
    textTransform: 'capitalize',
  },
  // AI Insights Styles
  insightsSection: {
    padding: 20,
    paddingTop: 0,
  },
  insightsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  insightsTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  seeAllText: {
    fontSize: 14,
    color: '#4A90E2',
    fontWeight: '500',
  },
  insightsModalContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
    marginTop: height * 0.2,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  insightsList: {
    padding: 20,
  },
  insightItem: {
    flexDirection: 'row',
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  insightIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  insightContent: {
    flex: 1,
  },
  insightItemTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  insightDescription: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
    lineHeight: 18,
  },
  insightAction: {
    fontSize: 12,
    color: '#4A90E2',
    fontWeight: '500',
    marginBottom: 8,
  },
  insightFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  impactBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  impactText: {
    fontSize: 10,
    color: '#fff',
    fontWeight: '600',
  },
  confidenceText: {
    fontSize: 10,
    color: '#64748B',
    fontWeight: '500',
  },
  // Team Analytics Styles
  teamOverview: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  teamMetric: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  teamMetricValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  teamMetricLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 8,
  },
  teamProgressBar: {
    width: 60,
    height: 4,
    backgroundColor: '#E2E8F0',
    borderRadius: 2,
    overflow: 'hidden',
  },
  teamProgressFill: {
    height: '100%',
    backgroundColor: '#10B981',
    borderRadius: 2,
  },
  departmentCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  departmentTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  departmentItems: {
    gap: 12,
  },
  departmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  departmentLabel: {
    fontSize: 14,
    color: '#0F172A',
    width: 80,
  },
  departmentBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#E2E8F0',
    borderRadius: 3,
    overflow: 'hidden',
  },
  departmentFill: {
    height: '100%',
    backgroundColor: '#4A90E2',
    borderRadius: 3,
  },
  departmentCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    width: 30,
    textAlign: 'right',
  },
  // Financial Analytics Styles
  financialGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  financialCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    width: (width - 52) / 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  financialValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 8,
    marginBottom: 4,
  },
  financialLabel: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  financialChange: {
    fontSize: 12,
    fontWeight: '600',
    color: '#10B981',
  },
  healthMetrics: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  healthMetric: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  healthMetricValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
    marginBottom: 4,
  },
  healthMetricLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  financialSummary: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  financialSummaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 16,
  },
  healthScore: {
    alignItems: 'center',
    marginBottom: 20,
  },
  healthScoreValue: {
    fontSize: 32,
    fontWeight: '700',
    color: '#10B981',
  },
  healthScoreLabel: {
    fontSize: 14,
    color: '#64748B',
    marginTop: 4,
  },
  starRating: {
    flexDirection: 'row',
    gap: 2,
  },
  // Client Stats Styles
  clientStatsGrid: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  clientStatCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  clientStatValue: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
    marginTop: 8,
    marginBottom: 4,
  },
  clientStatLabel: {
    fontSize: 12,
    color: '#64748B',
    textAlign: 'center',
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.6,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  modalContent: {
    padding: 20,
  },
  periodOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
  },
  periodOptionSelected: {
    backgroundColor: '#F0F8FF',
  },
  periodOptionText: {
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 12,
    flex: 1,
  },
  periodOptionTextSelected: {
    color: '#4A90E2',
    fontWeight: '600',
  },
  exportOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginBottom: 8,
    backgroundColor: '#F8FAFC',
  },
  exportOptionInfo: {
    flex: 1,
    marginLeft: 12,
  },
  exportOptionText: {
    fontSize: 16,
    color: '#0F172A',
  },
  exportOptionSubtext: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
  },
});